/**
 * ────────────────────────────────────────────────────────────────────────────────
 * DASHBOARD SQL RULES (Option A: runtime injection) — Template Tags Reference
 * ────────────────────────────────────────────────────────────────────────────────
 * WHAT WE DO
 * - Keep Dataset.QueryTemplate as the source of truth.
 * - At runtime we inject filters (dataset default, visual, global) into the SQL.
 * - Only <|TableName|> is required. All other tags are OPTIONAL.
 *
 * PRIMARY TAGS
 * - <|TableName|>
 *     Replaced with the resolved table name, quoted:  "MyTable"
 *
 * OPTIONAL COMBINED TAGS (easy mode)
 * - <|Where|>
 *     Replaced with a full WHERE clause (or removed if empty), e.g.:
 *       WHERE "Region" IN ('US','EU') AND "Status" <> 'Cancelled'
 * - <|Having|>
 *     Replaced with a full HAVING clause (or removed if empty). Only applied if
 *     the query groups/aggregates (i.e., has GROUP BY).
 *
 * OPTIONAL PER-LAYER TAGS (advanced / granular placement)
 *   NOTE: Use these ONLY if you need to position each layer separately.
 *   They are treated as **fragments** that expand to `AND (<clause>)` when
 *   a seed WHERE/HAVING exists nearby. If you include them, structure like:
 *
 *   WHERE 1=1
 *   <|WhereDefault|>   // expands to: AND (<dataset-default-filters>)
 *   <|WhereVisual|>    // expands to: AND (<visual-filters>)
 *   <|WhereGlobal|>    // expands to: AND (<global-filters>)
 *
 *   GROUP BY ...
 *   HAVING 1=1
 *   <|HavingDefault|>  // expands to: AND (<dataset-default-havings>)
 *   <|HavingVisual|>   // expands to: AND (<visual-havings>)
 *   <|HavingGlobal|>   // expands to: AND (<global-havings>)
 *
 *   Per-layer tags:
 *     - <|WhereDefault|>, <|WhereVisual|>, <|WhereGlobal|>
 *     - <|HavingDefault|>, <|HavingVisual|>, <|HavingGlobal|>
 *
 *   If you place per-layer tags WITHOUT a seed WHERE/HAVING, our injector will:
 *     • auto-create WHERE ... before GROUP/ORDER/LIMIT when any WHERE-fragment exists
 *     • for HAVING, auto-create only if a GROUP BY is present
 *
 * OPERATOR SUPPORT (FilterItem.Operator; case-insensitive)
 *   eq, neq, lt, lte, gt, gte, contains, startsWith, endsWith,
 *   in, notIn, between, isNull, notNull
 * - Identifiers are quoted: "My Column"
 * - Literals are escaped; numbers/booleans/null preserved
 * - Consider switching LIKE → ILIKE if you want case-insensitive matching
 *
 * WHERE vs HAVING ROUTING
 * - WHERE: row-level predicates on raw columns (Date, Region, Status…)
 * - HAVING: aggregate-level predicates (e.g., SUM/COUNT aliases used by the visual)
 * - Today we route everything to WHERE by default. To support HAVING, either:
 *     (a) tag FilterItem with Scope: 'where' | 'having', or
 *     (b) detect when FilterItem.Column matches a measure alias (e.g., total_sales)
 *
 * AUTO-INJECTION (when tags are absent)
 * - If the template has no <|Where|>/<|Where*|> tags:
 *     • Append to existing WHERE with AND, or insert a new WHERE before GROUP/ORDER/LIMIT/EOF
 * - If the template has no <|Having|>/<|Having*|> tags but has GROUP BY:
 *     • Append to existing HAVING with AND, or insert a new HAVING after GROUP BY
 *
 * EXAMPLES
 * 1) Minimal template (no filter tags; auto-injection)
 *    SELECT d, SUM(sales) AS total
 *    FROM <|TableName|>
 *    GROUP BY d
 *
 *    → runtime may produce:
 *      SELECT d, SUM(sales) AS total
 *      FROM "FactSales"
 *      WHERE "Region" IN ('US','EU') AND "Status" <> 'Cancelled'
 *      GROUP BY d
 *
 * 2) Granular placement with per-layer tags
 *    SELECT *
 *    FROM <|TableName|>
 *    WHERE 1=1
 *    <|WhereDefault|>
 *    <|WhereVisual|>
 *    <|WhereGlobal|>
 *
 *    → expands to WHERE 1=1 AND (...) AND (...) AND (...)
 *
 * AI GENERATOR HINTS (future)
 * - Always include FROM <|TableName|>.
 * - Prefer combined tags (<|Where|>, <|Having|>) unless you need custom placement.
 * - Do NOT bake user filters into the template; they’re injected at runtime.
 * - Name aggregate aliases (e.g., AS total) so HAVING filters can target them.
 */


import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSharedData } from '../../hooks/useSharedData';
import { hydrateOption } from '../../eChartsHydrator';
import { useDashboardDomainContext } from '../../DashboardDomainProvider';
import useDataset from '../../../../engine/dataset/hooks/useDataset';
import { buildClause, injectWhereHaving } from '../../sqlFilterUtils';

export const useDynamicChartData = (visual) => {
  const { datasets, visualTypes, visualOptionShells, visualOptionShellsDraft } = useSharedData();
  const { isViewMode } = useDashboardDomainContext();

  const {
    // engine state
    engineReady,
    engineError,
    initEngine,

    // dataset actions
    registerDataset,
    onRunQuery,
    statusByDatasetId,
    subscribeDataset,

    // shared history
    logInfo = () => {},
    logError = () => {},
    history,
  } = useDataset();

  const selectedDataset = useMemo(
    () => datasets?.find(ds => ds._id === visual?.DashboardDatasetId) || null,
    [datasets, visual?.DashboardDatasetId]
  );

  const visualType = useMemo(
    () => visualTypes?.find(vt => vt._id === visual?.VisualTypeId) || null,
    [visualTypes, visual?.VisualTypeId]
  );

  const shell = useMemo(
    () => (isViewMode ? visualOptionShells : visualOptionShellsDraft)?.find(x => x._id === visual?._id) ?? null,
    [isViewMode, visualOptionShells, visualOptionShellsDraft, visual?._id]
  );

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("Waiting to start…");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const registeredAttemptRef = useRef(new Set());
  const lastRunKeyRef = useRef("");
  const triedInitRef = useRef(false);

  const buildCanonicalToSource = useCallback((visualObj, dsObj) => {
    const out = {};
    const byCanon = new Map();

    const vcols = Array.isArray(visualObj?.VisualColumns) ? visualObj.VisualColumns : [];
    for (const row of vcols) {
      const canon = row?.Canonical;
      const field = row?.FieldName;
      if (!canon || canon === "none" || !field) continue;
      const cur = byCanon.get(canon);
      if (!cur || row?.IsDefaultForCanonical) {
        byCanon.set(canon, { field, isDefault: !!row?.IsDefaultForCanonical });
      }
    }
    for (const [canon, { field }] of byCanon.entries()) out[canon] = field;

    if (!out.x || !out.y || !out.z) {
      const cols = Array.isArray(dsObj?.Columns) ? dsObj.Columns : [];
      for (const col of cols) {
        const axis = col?.Axis;
        const field = col?.FieldName;
        if (!field) continue;
        if ((axis === "x" || axis === "y" || axis === "z") && !out[axis]) out[axis] = field;
      }
    }

    if (!out.SeriesName) {
      const cols = Array.isArray(dsObj?.Columns) ? dsObj.Columns : [];
      const primary = cols.find(c => c?.PrimaryField && c?.FieldName);
      if (primary) out.SeriesName = primary.FieldName;
    }

    return out;
  }, []);

  const canonicalToSource = useMemo(
    () => buildCanonicalToSource(visual, selectedDataset),
    [buildCanonicalToSource, visual, selectedDataset]
  );

// Base SQL (apply Option A rules: inject WHERE/HAVING at runtime)
const baseSql = useMemo(() => {
  if (!selectedDataset?._id) return "";

  // 1) Start from the saved template (or a safe default)
  const template = String(selectedDataset?.QueryTemplate || "SELECT * FROM <|TableName|>").trim();

  // 2) Fill <|TableName|> if present
  const tableName = selectedDataset?.TableName || "ds_" + selectedDataset._id;
  const withTable = template.replace(/<\|TableName\|>/g,`"${String(tableName).replace(/"/g, '""')}"`);

  // 3) Gather filters (visual + global). If you don’t have global yet, it’s just [].
  const visualFilters = Array.isArray(visual?.Filters) ? visual.Filters : [];

  // If your DomainProvider exposes global filters, grab them:
  // const { globalFilters = [] } = useDashboardDomainContext();
  // For now, fallback to [] if you don’t have them wired yet:
  const globalFilters = []; // TODO: replace with real global filters source

  // 4) Build WHERE (AND-joined)
  const whereAll = [buildClause(visualFilters), buildClause(globalFilters)]
    .filter(Boolean)
    .join(" AND ");

  // 5) HAVING (leave empty unless you explicitly route aggregate filters)
  const havingAll = ""; // e.g., route filters targeting measure aliases here

  // 6) Inject into the final SQL (handles existing WHERE/HAVING or inserts them)
  const sql = injectWhereHaving(withTable, { whereAll, havingAll });
  logInfo("Sql to execute: " + sql);
  return sql;
}, [
  selectedDataset?._id,
  selectedDataset?.QueryTemplate,
  selectedDataset?.TableName,
  selectedDataset?.table_name,
  visual?.Filters,
  // add your real global filters dep here when wired, e.g. globalFilters
]);

  // Reset first-load gate when dataset or SQL changes
  useEffect(() => {
    setHasLoadedOnce(false);
    lastRunKeyRef.current = "";
    setRows([]);
    setStatus("Waiting to start…");
  }, [selectedDataset?._id, baseSql]);

  // Ensure engine init starts if needed
  useEffect(() => {
    if (!engineReady && !engineError && typeof initEngine === 'function' && !triedInitRef.current) {
      triedInitRef.current = true;
      setIsLoading(true);
      setStatus("Starting data engine…");
      void initEngine(); // safe if provider already auto-inits
    }
  }, [engineReady, engineError, initEngine]);

  // ======= Initial load (guarded by engineReady, runKey & registration) =======
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!selectedDataset?._id) {
        setIsLoading(false);
        setRows([]);
        setStatus("Please choose a dataset to continue.");
        lastRunKeyRef.current = "";
        return;
      }

      // Wait for the engine; keep overlay up
      if (!engineReady) {
        setIsLoading(true);
        setStatus(engineError ? "We couldn’t start the data engine." : "Starting data engine…");
        return;
      }

      const dsid = selectedDataset._id;
      const sql = baseSql;
      const runKey = `${dsid}::${sql}`;
      if (lastRunKeyRef.current === runKey) return;

      setIsLoading(true);
      setStatus("Selecting your dataset…");

      let engineNotReadyDuringRun = false;

      try {
        const engineState = statusByDatasetId?.[dsid]?.state;
        if (engineState !== "registered" && engineState !== "ready" && !registeredAttemptRef.current.has(dsid)) {
          setStatus("Connecting your dataset to the engine…");
          registeredAttemptRef.current.add(dsid);
          await registerDataset(selectedDataset, { refreshOnAdd: false, defaultQuery: selectedDataset.QueryTemplate });
        }

        setStatus("Fetching your data…");
        const r = await onRunQuery(dsid, sql);

        if (!cancelled) {
          const next = Array.isArray(r) ? r : [];
          setRows(next);
          setStatus("Preparing your chart…");
          lastRunKeyRef.current = runKey;
          setHasLoadedOnce(true);
        }
      } catch (e) {
        const msg = String(e?.message || e || "");
        const looksNotReady = /engine[\s_-]*not[\s_-]*ready/i.test(msg);
        if (!cancelled) {
          if (looksNotReady || !engineReady) {
            engineNotReadyDuringRun = true;
            setStatus("Starting data engine…");
            // keep spinner; do NOT mark as error
          } else {
            setRows([]);
            setStatus("We couldn’t load your data right now. Please try again.");
            logError('Initial query failed', { error: msg }, selectedDataset?._id);
          }
        }
      } finally {
        if (!cancelled) {
          // Keep overlay if engine wasn't ready; otherwise finalize normally
          if (!engineNotReadyDuringRun) {
            setIsLoading(false);
            if (lastRunKeyRef.current === runKey && rows.length >= 0 && !status.startsWith("We couldn’t")) {
              setStatus("All set.");
            }
          } else {
            setIsLoading(true); // remain loading while engine comes up
          }
        }
      }
    }

    void load();
  }, [
    engineReady,
    engineError,
    selectedDataset?._id,
    baseSql,
    statusByDatasetId?.[selectedDataset?._id]?.state,
    registerDataset,
    onRunQuery,
    logError,
  ]);

  // -------------------------------------------------------------
  // Re-query helper (coalesced) — run visual SQL after updates
  // -------------------------------------------------------------
  const requeryInflightRef = useRef(false);

  const requeryFromVisual = useCallback(async (why = "dataset:update") => {
    const dsid = selectedDataset?._id;
    if (!dsid || !baseSql) return;
    if (!engineReady) return; // wait for engine
    if (requeryInflightRef.current) return;

    requeryInflightRef.current = true;
    logInfo('Requery start', { why, sqlPreview: baseSql.slice(0, 200) }, dsid);

    try {
      const r = await onRunQuery(dsid, baseSql);
      const next = Array.isArray(r) ? r : [];
      setRows(next);
      setStatus("Live");
      setHasLoadedOnce(true);
      logInfo('Requery done', { why, rows: next.length }, dsid);
    } catch (e) {
      setStatus("We couldn’t refresh this chart right now.");
      logError('Requery failed', { why, error: String(e?.message || e) }, dsid);
    } finally {
      requeryInflightRef.current = false;
    }
  }, [engineReady, selectedDataset?._id, baseSql, onRunQuery, logInfo, logError]);

  // ======= Live updates: subscribe only when engine ready =======
  useEffect(() => {
    const dsid = selectedDataset?._id;
    if (!engineReady || !dsid || typeof subscribeDataset !== 'function') return;

    logInfo('Subscribed to dataset updates', undefined, dsid);

    const unsubscribe = subscribeDataset(dsid, (payload) => {
      const rowsInEvent = Array.isArray(payload?.rows) ? payload.rows.length : 0;
      logInfo('Dataset update event', {
        rev: payload?.rev,
        rowsInEvent,
        rowCount: payload?.rowCount,
        at: payload?.at ?? Date.now(),
      }, dsid);

      void requeryFromVisual("dataset:update");
    });

    return () => {
      logInfo('Unsubscribed from dataset updates', undefined, dsid);
      try { unsubscribe?.(); } catch {}
    };
  }, [engineReady, selectedDataset?._id, subscribeDataset, requeryFromVisual, logInfo]);

  const hydratedOptionJson = useMemo(() => {
    if (!shell) return {};
    return hydrateOption(shell, rows, canonicalToSource);
  }, [shell, rows, canonicalToSource, visualType?.Type]);

  // Keep overlay until first successful data load
  const option = hasLoadedOnce && shell ? { ...shell, OptionJson: hydratedOptionJson } : null;

  return { option, isLoading, status, rows, selectedDataset, history };
};
